import { useState } from "react";
import { toast } from "sonner";
import { CONTACTO, waLink, track } from "@/data/content";
import { useLeadSubmit } from "@/lib/useLeadSubmit";
import { useCertero } from "@/context/CerteroContext";

const TIPOS_LABEL = {
  cotizacion: "Cotizar equipo",
  contacto: "Contacto",
  soporte: "Pedir soporte técnico",
  emergencia: "Emergencia en campo",
  academia: "Inscripción CERTERO Academy",
  servicio: "Solicitar servicio",
  campana: "Agendar campaña",
  demo: "Reservar demostración",
  repuesto: "Consultar repuesto",
};

const INTERESES = [
  "DJI Agras T100",
  "DJI Agras T70P",
  "DJI Agras T55",
  "DJI Mavic 3M",
  "Paquete llave en mano",
  "Servicio con drones",
  "Soporte técnico",
  "CERTERO Academy",
  "Otro",
];

export default function LeadForm({ tipo, interes, buttonLabel, testidPrefix = "lead", compact = false }) {
  const { setPrivacyOpen, closeLead } = useCertero();
  const { sending, ok, error, submit, reset } = useLeadSubmit();
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    interes: interes || "",
    mensaje: "",
    media: "",
    consent: false,
    website: "",
  });

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.consent) return;
    track("form_submit", { tipo });
    const mensajeFinal = form.media
      ? `${form.mensaje || ""}${form.mensaje ? "\n\n" : ""}Fotos o video: ${form.media}`.trim()
      : form.mensaje;
    submit(
      { ...form, mensaje: mensajeFinal, tipo, interes: form.interes || interes || null },
      () => toast.success("Solicitud registrada. El equipo CERTERO lo contactará.")
    );
  };

  const waMsgPorTipo = {
    cotizacion: "acabo de enviar una solicitud de cotización desde la web",
    contacto: "acabo de escribir desde la web",
    soporte: "necesito soporte técnico para mi equipo — le puedo enviar fotos o video por aquí",
    emergencia: "TENGO UNA EMERGENCIA EN CAMPO con mi dron — necesito atención inmediata. Le envío fotos o video del problema",
    academia: "quiero información de CERTERO Academy",
    servicio: "quiero coordinar un servicio con drones",
    campana: "quiero agendar una campaña de aplicación",
    demo: "quiero reservar una demostración en mi zona",
    repuesto: "quiero consultar por un repuesto o accesorio original",
  };

  const onSuccessWhatsApp = () => {
    track("form_whatsapp_continuar", { tipo });
    window.open(waLink(`Hola CERTERO SRL, ${waMsgPorTipo[tipo] || "acabo de enviar una solicitud desde la web"}.`), "_blank");
  };

  if (ok) {
    return (
      <div data-testid={`${testidPrefix}-success`} className="flex flex-col items-start gap-5 py-6">
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-certero bg-certero/10">
          <svg viewBox="0 0 24 24" className="h-6 w-6 text-certero" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
        </span>
        <div>
          <h3 className="font-display text-xl font-bold uppercase tracking-wide text-mist">Solicitud recibida</h3>
          <p className="mt-2 text-sm leading-relaxed text-smoke">
            Su solicitud quedó registrada y el equipo de CERTERO lo contactará a la brevedad. Si
            prefiere, continúe ahora mismo por WhatsApp.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid={`${testidPrefix}-success-whatsapp`}
            onClick={onSuccessWhatsApp}
            className="inline-flex h-11 items-center rounded-full bg-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-[#4bc443]"
          >
            Continuar por WhatsApp
          </button>
          <button
            data-testid={`${testidPrefix}-success-close`}
            onClick={() => { closeLead(); reset(); }}
            className="inline-flex h-11 items-center rounded-full border border-hud px-6 font-mono text-[11px] uppercase tracking-[0.2em] text-mist/80 transition-colors duration-300 hover:border-certero hover:text-certero"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  const inputCls =
    "h-12 w-full border border-hud bg-abyss px-4 text-sm text-mist placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-certero";
  const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-smoke";

  return (
    <form data-testid={`${testidPrefix}-form`} onSubmit={onSubmit} className="flex flex-col gap-4">
      <div className={compact ? "flex flex-col gap-4" : "grid gap-4 sm:grid-cols-2"}>
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-nombre`}>Nombre completo *</label>
          <input
            id={`${testidPrefix}-nombre`}
            data-testid={`${testidPrefix}-input-nombre`}
            className={inputCls}
            value={form.nombre}
            onChange={set("nombre")}
            required
            minLength={2}
            autoComplete="name"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-telefono`}>Teléfono / WhatsApp *</label>
          <input
            id={`${testidPrefix}-telefono`}
            data-testid={`${testidPrefix}-input-telefono`}
            className={inputCls}
            value={form.telefono}
            onChange={set("telefono")}
            required
            minLength={5}
            type="tel"
            autoComplete="tel"
            placeholder="+591 ..."
          />
        </div>
      </div>
      <div className={compact ? "flex flex-col gap-4" : "grid gap-4 sm:grid-cols-2"}>
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-email`}>Correo (opcional)</label>
          <input
            id={`${testidPrefix}-email`}
            data-testid={`${testidPrefix}-input-email`}
            className={inputCls}
            value={form.email}
            onChange={set("email")}
            type="email"
            autoComplete="email"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-interes`}>Interés</label>
          <select
            id={`${testidPrefix}-interes`}
            data-testid={`${testidPrefix}-input-interes`}
            className={`${inputCls} appearance-none`}
            value={form.interes}
            onChange={set("interes")}
          >
            <option value="">Seleccione (opcional)</option>
            {INTERESES.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`${testidPrefix}-mensaje`}>Mensaje</label>
        <textarea
          id={`${testidPrefix}-mensaje`}
          data-testid={`${testidPrefix}-input-mensaje`}
          className="w-full min-h-[96px] border border-hud bg-abyss p-4 text-sm text-mist placeholder:text-smoke/60 outline-none transition-colors duration-300 focus:border-certero"
          value={form.mensaje}
          onChange={set("mensaje")}
          placeholder="Cuéntenos sobre su cultivo, hectáreas o el equipo que le interesa."
          maxLength={3000}
        />
      </div>

      {(tipo === "soporte" || tipo === "emergencia") && (
        <div>
          <label className={labelCls} htmlFor={`${testidPrefix}-media`}>
            Enlace a fotos o video del problema (Drive/WhatsApp, opcional)
          </label>
          <input
            id={`${testidPrefix}-media`}
            data-testid={`${testidPrefix}-input-media`}
            className={inputCls}
            value={form.media || ""}
            onChange={set("media")}
            placeholder="https://..."
            type="url"
          />
        </div>
      )}

      {/* Honeypot antispam — invisible para personas */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <label className="flex items-start gap-3 text-xs leading-relaxed text-smoke">
        <input
          type="checkbox"
          data-testid={`${testidPrefix}-consent`}
          checked={form.consent}
          onChange={set("consent")}
          required
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#3baa34]"
        />
        <span>
          Acepto que CERTERO SRL utilice mis datos para responder a esta solicitud, conforme a su
          política de privacidad.
          <button
            type="button"
            onClick={() => setPrivacyOpen(true)}
            className="ml-2 text-certero underline underline-offset-2 hover:text-[#4bc443]"
            data-testid={`${testidPrefix}-privacidad-link`}
          >
            Ver política
          </button>
        </span>
      </label>

      {error && (
        <p data-testid={`${testidPrefix}-error`} role="alert" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid={`${testidPrefix}-btn-submit`}
        disabled={sending || !form.consent}
        className="inline-flex items-center justify-center rounded-full bg-certero px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,opacity,transform] duration-300 hover:bg-[#4bc443] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {sending ? "Enviando..." : buttonLabel || "Enviar solicitud"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke/60">
        También puede escribirnos directo: WhatsApp {CONTACTO.whatsappDisplay}
      </p>
    </form>
  );
}
